import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  Link,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import { getAccounts } from '../services/api';
import type { AccountWithAMICount } from '../types/api';
import Navbar from '../components/Navbar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';

const Accounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<AccountWithAMICount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchAccounts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await getAccounts();
        if (response.error) {
          setError(response.error);
        } else {
          // Sort accounts by lastSeen in descending order
          const sortedAccounts = response.data.accounts.sort((a, b) => {
            const dateA = new Date(a.lastSeen || 0).getTime();
            const dateB = new Date(b.lastSeen || 0).getTime();
            return dateB - dateA;
          });
          setAccounts(sortedAccounts);
        }
      } catch (err) {
        setError('Failed to fetch accounts');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  const handleAccountClick = (accountId: string) => {
    navigate(`/account/${accountId}`);
  };

  const filteredAccounts = showAll
    ? accounts
    : accounts.filter((account) => account.amiCount < 25);

  // Updated summary calculations for new groupings
  const count1 = accounts.filter(a => a.amiCount === 1).length;
  const count2to10 = accounts.filter(a => a.amiCount >= 2 && a.amiCount <= 10).length;
  const count11to100 = accounts.filter(a => a.amiCount >= 11 && a.amiCount <= 100).length;
  const count101to1000 = accounts.filter(a => a.amiCount >= 101 && a.amiCount <= 1000).length;
  const countOver1000 = accounts.filter(a => a.amiCount > 1000).length;

  const barData = [
    { label: '1', value: count1 },
    { label: '2-10', value: count2to10 },
    { label: '11-100', value: count11to100 },
    { label: '101-1000', value: count101to1000 },
    { label: '>1000', value: countOver1000 },
  ];
  const COLORS = ['#1976d2', '#dc004e', '#ffb300', '#388e3c', '#7b1fa2'];

  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mb: 4 }}>
        <Navbar />
      </Box>
      <Typography variant="h4" gutterBottom>
        AWS Accounts
      </Typography>
      <Typography color="text.secondary" paragraph>
        List of AWS accounts and their associated Image counts
      </Typography>
      <FormControlLabel
        control={<Switch checked={showAll} onChange={() => setShowAll((v) => !v)} />}
        label={showAll ? 'Showing All Accounts (Toggle to show only accounts with < 25 Images)' : 'Only showing accounts with < 25 Images (Toggle to show all)'}
        sx={{ mb: 2 }}
      />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">Accounts with 1 AMI</Typography>
            <Typography variant="h4">{count1}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">Accounts with 2-10 AMIs</Typography>
            <Typography variant="h4">{count2to10}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">Accounts with 11-100 AMIs</Typography>
            <Typography variant="h4">{count11to100}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">Accounts with 101-1000 AMIs</Typography>
            <Typography variant="h4">{count101to1000}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">Accounts with {'>'}1000 AMIs</Typography>
            <Typography variant="h4">{countOver1000}</Typography>
          </Paper>
        </Grid>
      </Grid>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>Accounts by AMI Count</Typography>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={barData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis allowDecimals={false} />
            <RechartsTooltip />
            <Bar dataKey="value">
              {barData.map((_, idx) => (
                <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Account ID</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Image Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAccounts.map((account) => (
              <TableRow 
                key={account.accountId}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleAccountClick(account.accountId)}
              >
                <TableCell component="th" scope="row">
                  <Link
                    component="button"
                    variant="body1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAccountClick(account.accountId);
                    }}
                    sx={{ textDecoration: 'none' }}
                  >
                    {account.accountId}
                  </Link>
                </TableCell>
                <TableCell align="right">{account.amiCount}</TableCell>
              </TableRow>
            ))}
            {filteredAccounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No accounts found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Accounts; 